import { useEffect, useState } from "react";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useSelector } from "react-redux";
import { findSaldoCantinaByAlunoCodigo, findSaldoCantinaByCurrentUser } from "./CantinaSaldo/api-cantina-saldo";
import { findCantinaCreditoByAlunoCodigo, findCantinaCreditoByCurrentUser } from "./CantinaCredito/api-cantina-credito";
import { findCantinaExtratoByAlunoCodigo, findCantinaExtratoByCurrentUser } from "./CantinaExtrato/api-cantina-extrato";
import { findCupomCantinaByAlunoCodigo, findCupomCantinaByCurrentUser } from "./CupomCantina/api-cupom-cantina";
import CantinaSaldoViewInsereCredito from "./CantinaSaldo/CantinaSaldoViewInsereCredito";
import CantinaCreditoPageView from "./CantinaCredito/CantinaCreditoPageView";
import CantinaExtratoPageView from "./CantinaExtrato/CantinaExtratoPageView";
import CupomCantinaPageView from "./CupomCantina/CupomCantinaPageView";
import { EMPTY_PAGE } from "./CantinaPageShared";
import AlunoFindDialog from "../Aluno/AlunoFindDialog";
import { ADMIN, ALUNO, RESPONSAVEL, SECRETARIA } from "../Usuario/Perfil";
import { errorMessage } from "../../util/ConfirmAlertUtil";
import Container from "../UI/Container";
import Loading from "../UI/Loading";

const CREDITO = "credito";
const EXTRATO = "extrato";
const COMPRAS = "compras";

// Para cada aba: busca do usuário logado (aluno) e busca por código do aluno.
const ABAS = {
  [CREDITO]: { porUsuario: findCantinaCreditoByCurrentUser, porAluno: findCantinaCreditoByAlunoCodigo },
  [EXTRATO]: { porUsuario: findCantinaExtratoByCurrentUser, porAluno: findCantinaExtratoByAlunoCodigo },
  [COMPRAS]: { porUsuario: findCupomCantinaByCurrentUser, porAluno: findCupomCantinaByAlunoCodigo },
};

const emptyBoxSx = {
  py: 6,
  textAlign: "center",
  color: "text.secondary",
  border: 1,
  borderStyle: "dashed",
  borderColor: "divider",
  borderRadius: 2,
};

/** De quem são os dados: o aluno logado, o aluno do responsável ou o aluno escolhido pela secretaria. */
const resolverAlvo = (usuario, alunoBuscado) => {
  if (usuario?.perfil === ALUNO) return { porUsuario: true, key: "usuario" };
  const codAluno =
    usuario?.perfil === RESPONSAVEL ? usuario.arAlunoSelecionado?.codigo : alunoBuscado?.codigo;
  return codAluno ? { codAluno, key: `aluno:${codAluno}` } : null;
};

const CantinaUsuarioContainer = () => {
  const usuario = useSelector((state) => state.usuario);
  const isGestao = usuario?.perfil === ADMIN || usuario?.perfil === SECRETARIA;
  const isResponsavel = usuario?.perfil === RESPONSAVEL;
  const [alunoBuscado, setAlunoBuscado] = useState(null);
  const [openSearchAluno, setOpenSearchAluno] = useState(false);
  const [cantinaSaldo, setCantinaSaldo] = useState(null);
  const [isLoadingSaldo, setIsLoadingSaldo] = useState(false);
  const [aba, setAba] = useState(CREDITO);
  const [pageNumber, setPageNumber] = useState(0);
  const [page, setPage] = useState(EMPTY_PAGE);
  const [isLoadingPage, setIsLoadingPage] = useState(false);

  const alvo = resolverAlvo(usuario, alunoBuscado);
  const alvoKey = alvo?.key ?? "";

  useEffect(() => {
    setCantinaSaldo(null);
    if (!alvo) return undefined;
    let active = true;
    setIsLoadingSaldo(true);
    const request = alvo.porUsuario ? findSaldoCantinaByCurrentUser() : findSaldoCantinaByAlunoCodigo(alvo.codAluno);
    request
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setCantinaSaldo(data);
        }
      })
      .finally(() => {
        if (active) setIsLoadingSaldo(false);
      });
    return () => {
      active = false;
    };
    // O alvo é recriado a cada render; a chave identifica quando ele muda de fato.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alvoKey]);

  useEffect(() => {
    setPage(EMPTY_PAGE);
    if (!alvo) return undefined;
    let active = true;
    setIsLoadingPage(true);
    const { porUsuario, porAluno } = ABAS[aba];
    const request = alvo.porUsuario ? porUsuario(pageNumber) : porAluno(alvo.codAluno, pageNumber);
    request
      .then((data) => {
        if (!active || !data) return;
        if (data.error) {
          errorMessage(data.message);
        } else {
          setPage(data);
        }
      })
      .finally(() => {
        if (active) setIsLoadingPage(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alvoKey, aba, pageNumber]);

  const handleAbaChange = (event, value) => {
    setAba(value);
    setPageNumber(0);
  };

  const handleTrocarAluno = (aluno) => {
    setAlunoBuscado(aluno);
    setPageNumber(0);
  };

  const handleChangePage = (event, newPage) => setPageNumber(newPage);

  const renderAlvo = () => {
    if (isGestao) {
      if (!alunoBuscado) return null;
      return (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar alt={alunoBuscado.nome}>{alunoBuscado.nome?.[0]}</Avatar>
            <Box flexGrow={1} minWidth={0}>
              <Typography variant="subtitle1" fontWeight={600} noWrap>
                {alunoBuscado.nome}
              </Typography>
              {alunoBuscado.codAlunoEscola && (
                <Typography variant="body2" color="text.secondary">
                  Código {alunoBuscado.codAlunoEscola}
                </Typography>
              )}
            </Box>
            <Button startIcon={<SwapHorizIcon />} onClick={() => setOpenSearchAluno(true)}>
              Trocar Aluno
            </Button>
          </Stack>
        </Paper>
      );
    }
    if (isResponsavel && usuario.arAlunoSelecionado) {
      return (
        <Typography variant="body2" color="text.secondary">
          Aluno: <strong>{usuario.arAlunoSelecionado.nome}</strong>
        </Typography>
      );
    }
    return null;
  };

  const renderSemAlvo = () => {
    if (isGestao) {
      return (
        <Box sx={emptyBoxSx}>
          <PersonSearchOutlinedIcon fontSize="large" />
          <Typography variant="body2" mt={1} mb={2}>
            Selecione um aluno para ver o saldo e as movimentações da cantina.
          </Typography>
          <Button variant="contained" startIcon={<PersonSearchOutlinedIcon />} onClick={() => setOpenSearchAluno(true)}>
            Procurar Aluno
          </Button>
        </Box>
      );
    }
    return (
      <Box sx={emptyBoxSx}>
        <Typography variant="body2">
          Nenhum aluno selecionado. Selecione um aluno na barra superior ou em Configurações.
        </Typography>
      </Box>
    );
  };

  return (
    <Container>
      <AlunoFindDialog openSearch={openSearchAluno} setOpenSearch={setOpenSearchAluno} setAluno={handleTrocarAluno} />

      <Box mb={3}>
        <Typography variant="h6">Cantina</Typography>
        <Typography variant="body2" color="text.secondary">
          Saldo, créditos, extrato e compras na cantina.
        </Typography>
      </Box>

      {!alvo ? (
        renderSemAlvo()
      ) : (
        <Stack spacing={3}>
          {renderAlvo()}

          {isLoadingSaldo ? (
            <Box py={3}>
              <Loading size={28} description="Carregando saldo..." />
            </Box>
          ) : (
            cantinaSaldo && (
              <CantinaSaldoViewInsereCredito cantinaSaldo={cantinaSaldo} podeInserirCredito={isResponsavel} />
            )
          )}

          <Box>
            <Tabs value={aba} onChange={handleAbaChange} sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
              <Tab value={CREDITO} label="Créditos" />
              <Tab value={EXTRATO} label="Extrato" />
              <Tab value={COMPRAS} label="Compras" />
            </Tabs>
            {aba === CREDITO && (
              <CantinaCreditoPageView pageCantinaCredito={page} handleChangePage={handleChangePage} isLoading={isLoadingPage} />
            )}
            {aba === EXTRATO && (
              <CantinaExtratoPageView pageCantinaExtrato={page} handleChangePage={handleChangePage} isLoading={isLoadingPage} />
            )}
            {aba === COMPRAS && (
              <CupomCantinaPageView pageCupomCantina={page} handleChangePage={handleChangePage} isLoading={isLoadingPage} />
            )}
          </Box>
        </Stack>
      )}
    </Container>
  );
};

export default CantinaUsuarioContainer;
